// ============================================================
// navCatalog — what the Section Navigator knows about each portal beyond its section list (CLB-080 S6,
// David's v8 prototype): the groups the sections sit in, an icon per section and the small tags.
// Keys are the ones the shell already uses (PORTAL_NAV: devView / salesView / projectsView / tab).
// A section that no group names is listed last, ungrouped — a new section never disappears.
// ============================================================
import { PORTAL_NAV } from '../../constants/portals';
import { describe } from './navModel';

const G = (name, keys) => ({ name, keys });

export const NAV_META = {
  catalog: { groups: [G('Browse', ['catalog', 'overview']), G('Lists', ['favorites', 'dev_requests'])], icon: { catalog: 'grid', overview: 'chart', favorites: 'star', dev_requests: 'inbox' } },
  developments: { groups: [G('Work', ['board', 'list', 'byDesigner']), G('Insight', ['analytics']), G('Keep', ['archive']), G('Admin', ['settings', 'mailIntel'])],
    icon: { board: 'board', list: 'list', byDesigner: 'users', analytics: 'chart', archive: 'archive', settings: 'gear', mailIntel: 'mail' }, gated: ['mailIntel'] },
  qc: { groups: [G('Work', ['reports', 'overview', 'my-reports', 'new-report']), G('Insight', ['data', 'my-stats']), G('Admin', ['settings'])],
    icon: { reports: 'sheet', overview: 'home', 'my-reports': 'sheet', 'new-report': 'send', data: 'chart', 'my-stats': 'chart', settings: 'gear' } },
  sales: { groups: [G('Overview', ['dashboard']), G('Explore', ['items', 'models', 'analytics']), G('Stock', ['inventory']), G('Data', ['data', 'events'])],
    icon: { dashboard: 'spark', items: 'rows', models: 'glasses', analytics: 'chart', inventory: 'box', data: 'ul', events: 'pulse' }, badge: { dashboard: 'NEW' } },
  projects: { groups: [G('Decide', ['selection', 'balance']), G('Send', ['sellsheets', 'decisionemails'])],
    icon: { selection: 'scale', sellsheets: 'sheet', decisionemails: 'mail', balance: 'swap' }, badge: { balance: 'NEW' } },
  // Orders: one column per world; the section icons are per world
  orders: { icon: {
    desk: { today: 'clock', models: 'glasses', followup: 'bell', export: 'download', reminders: 'bell', orders: 'sheet', print: 'rows', inbox: 'inbox', suppliers: 'truck', settings: 'gear', banks: 'layers', access: 'gear', health: 'pulse', datacenter: 'db', items: 'sheet', model: 'glasses', names: 'pencil' },
    book: { summary: 'chart', builder: 'layers', data: 'rows', insights: 'spark', compare: 'swap' },
    collection: { overview: 'home', wall: 'grid', table: 'rows', repeats: 'refresh', compare: 'swap', exceptions: 'warn', email: 'send' },
  }, twice: { 'book.compare': 'Collection', 'collection.compare': 'Orders Book' } },
};

const DEFAULT_KEY = { catalog: 'catalog', developments: 'board', qc: 'dashboard', sales: 'items', projects: 'selection' };

/** The section a portal opens on when nothing is pinned (the label the header's Home chip falls back to). */
export function defaultSectionKey(portalKey) { return DEFAULT_KEY[portalKey] || null; }

/** One row: the shell's section + description, icon, tags. `worldKey` only for Orders. */
export function decorate(portalKey, worldKey, s, isAdminOnly = false) {
  const meta = NAV_META[portalKey] || {};
  const d = describe(portalKey, worldKey, s);
  const icon = portalKey === 'orders' ? (meta.icon[worldKey] || {})[s.key] : (meta.icon || {})[s.key];
  const nav = PORTAL_NAV.find((p) => p.key === portalKey);
  const sub = nav && (nav.subsections || []).find((x) => (x.devView || x.salesView || x.projectsView || x.tab || x.key) === s.key);
  return {
    ...s, d, icon: icon || 'list',
    soon: /^coming soon$/i.test(d),
    gated: !!(meta.gated && meta.gated.includes(s.key)) || isAdminOnly,
    badge: (meta.badge && meta.badge[s.key]) || (sub && sub.badge) || null,
    twice: portalKey === 'orders' ? (meta.twice || {})[`${worldKey}.${s.key}`] || null : null,
  };
}

/** Groups for a flat portal: [{ name, rows }], with unlisted sections last and ungrouped. */
export function groupRows(portalKey, rows) {
  const meta = NAV_META[portalKey];
  if (!meta || !meta.groups) return [{ name: '', rows }];
  const seen = new Set();
  const out = [];
  meta.groups.forEach((g) => {
    const rs = g.keys.map((k) => rows.find((r) => r.key === k)).filter(Boolean);
    rs.forEach((r) => seen.add(r.key));
    if (rs.length) out.push({ name: g.name, rows: rs });
  });
  const rest = rows.filter((r) => !seen.has(r.key));
  if (rest.length) out.push({ name: out.length ? 'More' : '', rows: rest });
  return out;
}
